"use client";

import clsx from "clsx";
import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { FC } from "react";
import styles from "./text-with-image-richtex.module.css";

import {
  IAccordionSliderSection,
  IAwardsSection,
  IBannerSection,
  IScqaExplainerSection,
  IBlogsSection,
  ICallCtaSection,
  ICardSection,
  ICareerDetailSection,
  ICareerFormSection,
  ICertificatesSection,
  ICertifiedPentesterSection,
  ICommanCtaSection,
  IContentWithTabsSection,
  ICourseDeliverables,
  ICourseLocationSection,
  ICourseOverviewSection,
  ICoursePlanSection,
  ICourseTimingSection,
  ICurrentOpeningsSection,
  ICustomFormSection,
  ICyberSecuritySection,
  IEducationMaterialSection,
  IExploreCategorySection,
  IFaqSection,
  IHappyHoursSection,
  IHeroSliderSection,
  IIndustryCardSection,
  IInformationalSliderSection,
  IJourneySliderSection,
  ILatestBlogsSection,
  IMetricsSection,
  IStatsStripSection,
  ITrustedBySection,
  IPhilosophyDeliverableSection,
  IOfferSection,
  IPageComponent,
  IPartnerContactInformationSection,
  IPartnersFormSection,
  IPartnersSection,
  IPenetrationCasesSection,
  IProcessSection,
  IRegulationSection,
  IResourceDownloadFormSection,
  IReusableSection,
  IScheduleMeetSection,
  ISecurityIframeSection,
  IServiceListSection,
  IServicesSection,
  IServiceTabListSection,
  ISolutionSection,
  IStaticBlocksSection,
  ISuccessMetricsSection,
  ITakeSecurityAssessmentSection,
  ITeamContributionSection,
  ITeamSection,
  ITeamSpiritSection,
  ITextSection,
  ITextWithCardGridSection,
  ITextWithCardsSection,
  ITextWithEmbedSection,
  ITextWithGridSection,
  ITextWithImageGridSection,
  ITextWithImageSection,
  ITextWithTabsSection,
  ITwoColumnTabSection,
  ITwoColumnTextSection,
  IUpcomingCoursesSection,
} from "@/api/dtos/block-component.type";

import CardSection from "@/sections/about-us/CardSection";
// JourneySlider pulls in framer-motion (~50KB gzipped) but is only used on
// /about-us. Lazy-load it so other pages don't pay the bundle cost.
const JourneySlider = dynamic(
  () => import("@/sections/about-us/JourneySlider"),
);
import SuccessMetrics from "@/sections/about-us/SuccessMetrics";
import VisionMissionSection from "@/sections/about-us/VisionMissionSection";
import BannerSection from "@/sections/common/BannerSection";
import FaqSection from "@/sections/common/FaqSection";
import MetricsSectionComponent from "@/sections/common/MetricsSectionComponent";
import StatsStripSection from "@/sections/home/StatsStripSection";
import TrustedByStripSection from "@/sections/home/TrustedByStripSection";
import TextSection from "@/sections/common/TextSection";
import TextWithGridSection from "@/sections/common/TextWithGridSection";
import TextWithImage from "@/sections/common/TextWithImage";
import TextWithImageGrid from "@/sections/common/TextWithImageGrid";
import RedTeamingSection from "@/sections/home/RedTeamingSection";
import TextWithTabsSection from "@/sections/common/TextWithTabsSection";
import TwoColumnTextSection from "@/sections/common/TwoColumnTextSection";
import HowEngagementRunsSection from "@/sections/home/HowEngagementRunsSection";
import AccordianSliderSection from "@/sections/home/AccordianSliderSection";
import CallCtaSection from "@/sections/home/CallCtaSection";
import CertificatesSection from "@/sections/home/CertificatesSection";
import CertifiedPentestersSection from "@/sections/home/CertifiedPentestersSection";
import EducationMaterialSection from "@/sections/home/EducationMaterialSection";
import HeroSliderSection from "@/sections/home/HeroSliderSection";
import IndustryCardSection from "@/sections/home/IndustryCardSection";
import MaturityModelSection from "@/sections/home/MaturityModelSection";
import RatingSection from "@/sections/home/RatingSection";
import ScheduleMeetSection from "@/sections/home/ScheduleMeetSection";
import ServicesSection from "@/sections/home/ServicesSection";
import SolutionSection from "@/sections/home/SolutionSection";
import InformationSliderSection from "@/sections/industry/InformationSliderSection";
import RegulationSection from "@/sections/industry/RegulationSection";
import PenetrationCasesSection from "@/sections/services/PenetrationCasesSection";
import ProcessSection from "@/sections/services/ProcessSection";
import ScqaExplainerSection from "@/sections/services/ScqaExplainerSection";
import ReportingStandardSection from "@/sections/services/ReportingStandardSection";
import TextWithCardsSection from "@/sections/services/TextWithCardsSection";
import SecurityIframeSection from "@/sections/take-security-assessment/SecurityIframeSection";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";

// True only if `blocks` will render visible content. A non-empty array of
// paragraph blocks whose text is all whitespace still passes `description ?
// ... : undefined`, then renders as a zero-content prose div that takes one
// line of vertical space. Use this before wrapping in BlocksRenderer.
const hasMeaningfulContent = (blocks?: BlocksContent): boolean => {
  if (!blocks || blocks.length === 0) return false;
  return blocks.some((block) => {
    if (block.type !== "paragraph") return true;
    return block.children.some(
      (child) => "text" in child && child.text.trim().length > 0,
    );
  });
};

import { callCtaSectionData } from "@/content/CallCtaSection";
import { educationMaterialSectionData } from "@/content/EducationMaterialSection";
import { maturityModelSectionData } from "@/content/MaturityModelSection";
import { ratingSectionData } from "@/content/RatingSection";
import { ReportingStandardSectionData } from "@/content/ReportingStandardSection";
import { ScheduleMeetSectionData } from "@/content/ScheduleMeetSection";
import { textWithImageGridData } from "@/content/TextWithImageGridSection";
import { missionVisionData } from "@/content/VisionMissionSection";
import { env } from "@/env.mjs";
import BlogsWithFilterSection from "@/sections/blogs/BlogsWithFilterSection";
import ExploreCategoriesSection from "@/sections/blogs/ExploreCategoriesSection";
import FeaturedBlogsSection from "@/sections/blogs/FeaturedBlogsSection";
import LatestBlogsSection from "@/sections/blogs/LatestBlogsSection";
import CareerDetailSection from "@/sections/careers/CareerDetailSection";
// Form sections pull react-hook-form + reCAPTCHA + phone-input. They only
// render on specific pages — lazy-load to keep the global bundle slim.
const CareerForm = dynamic(() => import("@/sections/careers/CareerForm"));
import CommonCtaSection from "@/sections/careers/CommonCtaSection";
import CurrentOpeningSection from "@/sections/careers/CurrentOpeningSection";
const CustomFormSection = dynamic(
  () => import("@/sections/common/CustomFormSection"),
);
import TextWithEmbedSection from "@/sections/common/TextWithEmbedSection";
import CourseDeliverablesSection from "@/sections/course/CourseDeliverablesSection";
import CourseLocation from "@/sections/course/CourseLocation";
import CourseOverviewSection from "@/sections/course/CourseOverviewSection";
import CoursePlan from "@/sections/course/CoursePlan";
import CourseTimingSection from "@/sections/course/CourseTimingSection";
import CyberSecuritySection from "@/sections/course/CyberSecuritySection";
import OfferSection from "@/sections/course/OfferSection";
import UpcomingCoursesSection from "@/sections/course/UpcomingCoursesSection";
// GallerySection uses react-photo-album (~30KB gzipped) but is only on the
// /culture-and-team page. Lazy-load to keep other pages slim.
const GallerySection = dynamic(
  () => import("@/sections/culture-and-team/GallerySection"),
);
import TeamSection from "@/sections/culture-and-team/TeamSection";
import TeamSpiritSection from "@/sections/culture-and-team/TeamSpiritSection";
import TextWithCardGridSection from "@/sections/culture-and-team/TextWithCardGridSection";
import AwardsSection from "@/sections/home/AwardsSection";
import ServiceListSection from "@/sections/leadership/ServiceListSection";
import ServiceTabListSection from "@/sections/leadership/ServiceTabListSection";
import PartnerContactInfoSection from "@/sections/partners/PartnerContactInfoSection";
import PartnerSection from "@/sections/partners/PartnerSection";
const PartnersFormSection = dynamic(
  () => import("@/sections/partners/PartnersFormSection"),
);
import ContentWithTabsSection from "@/sections/resources/ContentWithTabsSection";
// ResourceDownloadFormSection pulls country-state-city (~500KB raw, ~80KB
// tree-shaken) + react-hook-form. Lazy-load to defer that from initial JS.
const ResourceDownloadFormSection = dynamic(
  () => import("@/sections/resources/ResourceDownloadFormSection"),
);
import ScheduleMeetingSection from "@/sections/schedule/ScheduleMeetingSection";
import TakeSecurityAssessment from "@/sections/take-security-assessment/TakeSecurityAssessment";
import { getBreadCrumbs } from "@/utils/breadcrumb.helper";
import { getImageUrl, getVideoUrl } from "@/utils/media.helper";
import { map, trimEnd } from "lodash";

export type IStandardComponentProps = {
  content: IPageComponent;
  pageName?: string;
  params?: Record<string, string>;
};

const socialMedia: Record<string, string> = {
  linkedin: "linkedin",
  twitter_x: "twitter",
  whatsapp: "whatsapp",
  mail: "gmail",
  microsoft_office_teams: "teams",
};

const StandardComponent: FC<IStandardComponentProps> = (props) => {
  const { content, pageName, params } = props;
  const path = usePathname();

  if (content.__component === "blocks.hero-slider-section") {
    const { heading, hero_slider } = content as IHeroSliderSection;

    return (
      <HeroSliderSection
        heading={heading}
        animatedHeadings={hero_slider?.map((item) => item.animated_headings)}
        buttons={hero_slider?.map(({ buttons }) => {
          return buttons
            ? {
                ...buttons,
                text: buttons.title,
                link: buttons.href,
                id: buttons.btn_id,
              }
            : undefined;
        })}
        background={hero_slider?.map((item) => ({
          image: getImageUrl(item.image),
          color: item.color,
        }))}
        description={hero_slider?.map(({ description }, idx) =>
          description ? (
            <BlocksRenderer key={idx} content={description} />
          ) : undefined,
        )}
      />
    );
  }

  if (content.__component === "blocks.banner-section") {
    const { heading, sub_heading, description, image } =
      content as IBannerSection;

    const breadcrumbs: { title: string; href?: string }[] = getBreadCrumbs(
      path,
      pageName,
    );

    return (
      <BannerSection
        breadcrumbs={
          path === "/take-security-assessment" ? undefined : breadcrumbs
        }
        heading={heading}
        subHeading={sub_heading}
        image={getImageUrl(image)}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.scqa-explainer") {
    return <ScqaExplainerSection content={content as IScqaExplainerSection} />;
  }

  if (content.__component === "blocks.industry-card-section") {
    const { background, industries, heading, description } =
      content as IIndustryCardSection;

    return (
      <IndustryCardSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        backgroundVariant={background}
        cards={industries?.map(({ icon, heading, description, button }) => {
          return {
            icon: icon?.name,
            heading: heading,
            description: description ? (
              <BlocksRenderer content={description} />
            ) : undefined,
            button: {
              ...button,
              text: button?.title,
              link: button?.href,
              id: button?.btn_id,
            },
          };
        })}
      />
    );
  }

  if (content.__component === "blocks.solution-section") {
    const { heading, description, SolutionCard } = content as ISolutionSection;

    return (
      <SolutionSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        solutions={SolutionCard?.map(
          ({ icon, heading, description, links }) => ({
            icon: icon?.name,
            list: links,
            heading,
            description: description ? (
              <BlocksRenderer content={description} />
            ) : undefined,
          }),
        )}
      />
    );
  }

  if (content.__component === "blocks.services-section") {
    const { heading, description, services, background } =
      content as IServicesSection;

    return (
      <ServicesSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        cards={services?.map((item) => ({
          heading: item.title,
          icon: item.icon?.name,
          description: item.description ? (
            <BlocksRenderer content={item.description} />
          ) : undefined,
          variant: item.background,
          size: item.size,
        }))}
        variant={background}
      />
    );
  }

  if (content.__component === "blocks.accordion-slider-section") {
    const { heading, articles } = content as IAccordionSliderSection;

    return (
      <AccordianSliderSection
        heading={heading}
        articles={articles?.map(({ heading, description }) => ({
          heading: heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
        images={articles?.map(({ image }) => {
          return image
            ? { src: getImageUrl(image), alt: image.name ?? "" }
            : undefined;
        })}
        buttons={articles?.map(({ button }) => ({
          ...button,
          text: button?.title,
          link: button?.href,
          id: button?.btn_id,
        }))}
      />
    );
  }

  if (content.__component === "blocks.metrics-section") {
    const { metrics_cards } = content as IMetricsSection;

    return <MetricsSectionComponent data={metrics_cards} />;
  }

  if (content.__component === "blocks.stats-strip-section") {
    const { stats } = content as IStatsStripSection;

    return <StatsStripSection stats={stats} />;
  }

  if (content.__component === "blocks.trusted-by-section") {
    const { label, clients } = content as ITrustedBySection;

    return <TrustedByStripSection label={label} clients={clients} />;
  }

  if (content.__component === "blocks.text-with-image-grid-section") {
    const { content: data, images } = content as ITextWithImageGridSection;

    return (
      <TextWithImageGrid
        heading={data?.heading}
        description={
          data?.description ? (
            <BlocksRenderer content={data.description} />
          ) : undefined
        }
        buttons={data?.button?.map((button) => ({
          ...button,
          text: button.title,
          link: button.href,
          id: button.btn_id,
        }))}
        images={images?.map((image) => ({
          src: getImageUrl(image),
          alt: image.name ?? "",
        }))}
      />
    );
  }

  if (content.__component === "blocks.certificates-section") {
    const { heading, images } = content as ICertificatesSection;

    return (
      <CertificatesSection
        heading={heading}
        certificates={images?.map((image) => ({
          logo: getImageUrl(image),
          heading: image.name,
        }))}
      />
    );
  }

  if (content.__component === "blocks.certified-pentester-section") {
    const { heading, description, button, pentesters } =
      content as ICertifiedPentesterSection;

    return (
      <CertifiedPentestersSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        button={{
          ...button,
          text: button?.title,
          link: button?.href,
          id: button?.btn_id,
        }}
        pentesters={pentesters?.map(({ designation, image, name, skills }) => ({
          name,
          designation,
          description: skills,
          imageUrl: image
            ? { src: getImageUrl(image), alt: image.name ?? "" }
            : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.text-with-image-section") {
    const {
      image,
      content: data,
      background,
      background_cover,
      image_position,
      image_size,
    } = content as ITextWithImageSection;

    // Route the home page's red-team block to the bespoke RedTeamingSection
    // component. Detection: heading or description mentions "red team" /
    // "adversary" / "malicious actor". Done in code (not via a new Strapi
    // field) so we avoid a CMS schema migration for a single section.
    const haystack = `${data?.heading ?? ""} ${JSON.stringify(
      data?.description ?? "",
    )}`.toLowerCase();
    const isRedTeam =
      /red[\s-]?team|adversary[\s-]?simulation|malicious[\s-]?actor/.test(
        haystack,
      );

    if (isRedTeam) {
      // The red-team section's own "Get the Sample Report" CTA is
      // sufficient — the standalone RedTeamCtaBand that previously
      // followed it has been removed per user feedback (too many CTAs
      // stacked, plus the pre-footer CallCtaSection already does the
      // final convert-or-leave job).
      return (
        <RedTeamingSection
          heading={data?.heading}
          description={
            data?.description ? (
              <BlocksRenderer content={data.description} />
            ) : undefined
          }
          image={
            image
              ? { src: getImageUrl(image), alt: image.name ?? "" }
              : undefined
          }
          buttons={data?.button?.map((button) => ({
            ...button,
            text: button.title,
            link: button.href,
            id: button.btn_id,
          }))}
        />
      );
    }

    return (
      <TextWithImage
        heading={data?.heading}
        description={
          data?.description ? (
            <BlocksRenderer content={data.description} />
          ) : undefined
        }
        richTextStyle={clsx(
          styles.richTextList,
          "prose-li:list-none prose-li:pl-0 prose-li:font-normal prose-li:text-black-900 dark:prose-li:list-disc prose-ul:[&>ul]:!pl-0 dark:prose-ul:[&>ul]:!pl-[18px]",
        )}
        image={
          image ? { src: getImageUrl(image), alt: image.name ?? "" } : undefined
        }
        mediaDirection={image_position}
        mediaHeight={image?.image.data?.attributes.height}
        mediaWidth={image?.image.data?.attributes.width}
        mediaRatio={image_size}
        background_cover={background_cover}
        background={background}
        buttons={data?.button?.map((button) => ({
          ...button,
          text: button.title,
          link: button.href,
          id: button.btn_id,
        }))}
      />
    );
  }

  if (content.__component === "blocks.text-section") {
    const { heading, description, background, list_items } =
      content as ITextSection;

    const listItems = list_items
      ?.map(({ points }) => [
        points
          ?.map(({ name }) => ({ name }))
          .slice(0, Math.round(points.length / 2)) ?? [],
        points
          ?.map(({ name }) => ({ name }))
          .slice(Math.round(points.length / 2)) ?? [],
      ])
      .flat();

    return (
      <TextSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        background={background}
        listItems={listItems}
      />
    );
  }

  if (content.__component === "blocks.informational-slider-section") {
    const { slides } = content as IInformationalSliderSection;

    return (
      <InformationSliderSection
        slides={slides.map(
          ({ heading, sub_heading, description, background, image }) => ({
            heading,
            subHeading: sub_heading,
            description: description ? (
              <BlocksRenderer content={description} />
            ) : undefined,
            variant: background,
            image: image
              ? { src: getImageUrl(image), alt: image.name ?? "" }
              : undefined,
          }),
        )}
      />
    );
  }

  if (content.__component === "blocks.text-with-grid-section") {
    const { heading, cards } = content as ITextWithGridSection;

    return (
      <TextWithGridSection
        heading={heading}
        cards={cards?.map(({ content, size }) => ({
          heading: content?.heading,
          description: content?.description ? (
            <BlocksRenderer content={content.description} />
          ) : undefined,
          size: size,
        }))}
      />
    );
  }

  if (content.__component === "blocks.regulation-section") {
    const { heading, description, regulation_tab } =
      content as IRegulationSection;

    return (
      <RegulationSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        regulations={regulation_tab.map(({ heading, description }) => ({
          name: heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.text-with-tabs-section") {
    const { heading, sub_heading, tab_cards } = content as ITextWithTabsSection;

    // Pass raw Strapi blocks (not pre-rendered) so TextWithTabsSection can
    // render each paragraph with a uniform structure. BlocksRenderer
    // faithfully reproduces every authoring quirk (empty paragraphs, line
    // breaks vs separate blocks) which caused inconsistent Impact↔Misuse
    // gaps between tabs.
    return (
      <TextWithTabsSection
        heading={heading}
        subheading={sub_heading}
        tabs={tab_cards.map(({ heading, description }) => ({
          heading,
          description,
        }))}
      />
    );
  }

  if (content.__component === "blocks.content-with-tabs-section") {
    const { heading, description, tabs } = content as IContentWithTabsSection;

    return (
      <ContentWithTabsSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        tabs={tabs.map(({ heading, description }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.two-column-tab-section") {
    const { heading, description, tabs } = content as ITwoColumnTabSection;

    return (
      <ContentWithTabsSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        tabs={tabs.map(
          ({
            heading,
            left_description,
            right_description,
            left_button,
            right_button,
          }) => ({
            heading,
            description: left_description ? (
              <BlocksRenderer content={left_description} />
            ) : undefined,
            rightDescription: right_description ? (
              <BlocksRenderer content={right_description} />
            ) : undefined,
            leftButton: left_button
              ? {
                  ...left_button.button,
                  text: left_button.button.title,
                  link: left_button.document.data
                    ? `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/document/download?documentID=${left_button.document.data.id}`
                    : left_button.button.href,
                  id: left_button.button.btn_id,
                  target: "_blank",
                }
              : undefined,
            rightButton: right_button
              ? {
                  ...right_button.button,
                  text: right_button.button.title,
                  link: right_button.document.data
                    ? `${trimEnd(env.NEXT_PUBLIC_SITE_URL, "/")}/api/document/download?documentID=${right_button.document.data.id}`
                    : right_button.button.href,
                  id: right_button.button.btn_id,
                  target: "_blank",
                }
              : undefined,
          }),
        )}
      />
    );
  }

  if (content.__component === "blocks.two-column-text-section") {
    const { column } = content as ITwoColumnTextSection;

    return (
      <TwoColumnTextSection
        cards={column.map(({ heading, description }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.philosophy-deliverable-section") {
    const {
      eyebrow,
      headline_main,
      emphasis_word,
      headline_followup,
      body,
      proof_bullets,
      primary_cta,
      secondary_cta,
      sample_brief_image,
      sample_brief_caption,
      sample_brief_callouts,
      subtitle_highlight,
    } = content as IPhilosophyDeliverableSection;

    // Map Strapi's shared.button (title/href/btn_id) into the
    // ITextButton shape (text/link/id) the section expects.
    const mapButton = (b?: typeof primary_cta) =>
      b
        ? {
            ...b,
            text: b.title,
            link: b.href,
            id: b.btn_id,
          }
        : undefined;

    return (
      <HowEngagementRunsSection
        eyebrow={eyebrow}
        headlineMain={headline_main}
        emphasisWord={emphasis_word}
        headlineFollowup={headline_followup}
        body={body}
        proofBullets={proof_bullets}
        primaryCta={mapButton(primary_cta)}
        secondaryCta={mapButton(secondary_cta)}
        sampleBriefImage={getVideoUrl(sample_brief_image)}
        sampleBriefImageAlt={
          sample_brief_image?.data?.attributes?.alternativeText
        }
        sampleBriefCaption={sample_brief_caption}
        sampleBriefCallouts={sample_brief_callouts}
        subtitleHighlight={subtitle_highlight}
      />
    );
  }

  if (content.__component === "blocks.process-section") {
    const { heading, goals } = content as IProcessSection;

    return (
      <ProcessSection
        heading={heading}
        processes={goals?.map(({ heading, description, icon, button }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
          icon: icon?.name,
          button: button?.map((item) => ({
            ...item,
            text: item.title,
            link: item.href,
            id: item.btn_id,
          })),
        }))}
      />
    );
  }

  if (content.__component === "blocks.penetration-cases-section") {
    const { heading, penetration_tabs } = content as IPenetrationCasesSection;

    return (
      <PenetrationCasesSection
        heading={heading}
        tabs={penetration_tabs?.map(({ name, description, penetrations }) => ({
          heading: name,
          description: hasMeaningfulContent(description) ? (
            <BlocksRenderer content={description!} />
          ) : undefined,
          badges: penetrations?.map((penetration) => ({
            ...penetration,
            text: penetration.title,
            link: penetration.href,
            id: penetration.btn_id,
          })),
        }))}
      />
    );
  }

  if (content.__component === "blocks.text-with-card-section") {
    const { cards } = content as ITextWithCardsSection;

    return (
      <TextWithCardsSection
        textWithCards={cards.map(({ heading, description, icon, button }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
          icon: icon?.name,
          button: button
            ? {
                ...button,
                text: button?.title,
                link: button?.href,
                id: button?.btn_id,
              }
            : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.partners-section") {
    const { heading, description, partners_card, background } =
      content as IPartnersSection;

    return (
      <PartnerSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        partners={partners_card.map(
          ({ heading, description, image, background, button }) => ({
            heading,
            description: description ? (
              <BlocksRenderer content={description} />
            ) : undefined,
            image: image
              ? { src: getImageUrl(image), alt: image.name ?? "" }
              : undefined,
            button: button
              ? {
                  ...button,
                  text: button?.title,
                  link: button?.href,
                  id: button?.btn_id,
                }
              : undefined,
            variant: background,
          }),
        )}
        variant={background}
      />
    );
  }

  if (content.__component === "blocks.partner-contact-information-section") {
    const { heading, contact_info } =
      content as IPartnerContactInformationSection;

    return (
      <PartnerContactInfoSection
        heading={heading}
        contacts={contact_info?.map(({ icon, name }) => ({
          icon: icon.name,
          name: name,
        }))}
      />
    );
  }

  if (content.__component === "blocks.journey-slider-section") {
    const { heading, description, our_journey } =
      content as IJourneySliderSection;

    return (
      <JourneySlider
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        cards={our_journey.map(({ time_line, heading, description }) => ({
          subHeading: time_line,
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.card-section") {
    const { heading, description, services } = content as ICardSection;

    return (
      <CardSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        cards={services.map(({ title, description, icon }) => ({
          icon: icon?.name,
          heading: title,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.success-metrics-section") {
    const { heading, description, metrics_cards } =
      content as ISuccessMetricsSection;

    return (
      <SuccessMetrics
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        successMetricsData={metrics_cards.map(({ name, number }) => ({
          name,
          number,
        }))}
      />
    );
  }

  if (content.__component === "blocks.text-with-embed-section") {
    const { heading, description, form } = content as ITextWithEmbedSection;

    return (
      <TextWithEmbedSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        form={form}
      />
    );
  }

  if (content.__component === "blocks.faq-section") {
    const { heading, faqs } = content as IFaqSection;

    return (
      <FaqSection
        heading={heading}
        questions={faqs.map(({ title, description }) => ({
          heading: title,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.common-cta-section") {
    const { content: data } = content as ICommanCtaSection;

    return (
      <CommonCtaSection
        heading={data.heading}
        description={
          data.description ? (
            <BlocksRenderer content={data.description} />
          ) : undefined
        }
        buttons={data?.button?.map((button) => ({
          ...button,
          text: button.title,
          link: button.href,
          id: button.btn_id,
        }))}
      />
    );
  }

  if (content.__component === "blocks.current-openings-section") {
    const { career_data, current_openings } =
      content as ICurrentOpeningsSection;

    if (current_openings) {
      return (
        <CurrentOpeningSection
          heading="Current Openings"
          cards={career_data?.map(({ attributes }) => ({
            button: {
              text: "Apply",
              link: path + `/${attributes.slug}`,
              variant: "yellow",
              id: `career-${attributes.slug}-navigation-button`,
            },
            heading: (
              attributes.career_details_page.filter(
                ({ __component }) => __component === "blocks.banner-section",
              )[0] as IBannerSection
            )?.heading,
            list: (
              attributes.career_details_page.filter(
                ({ __component }) =>
                  __component === "blocks.career-detail-section",
              )[0] as ICareerDetailSection
            ).criteria_list,
            isHiring: attributes.show_opening,
          }))}
        />
      );
    }
  }

  if (content.__component === "blocks.career-detail-section") {
    const {
      content: data,
      criteria_list,
      heading,
    } = content as ICareerDetailSection;

    return (
      <CareerDetailSection
        heading={heading}
        description={
          data.description ? (
            <BlocksRenderer content={data.description} />
          ) : undefined
        }
        criteriaList={criteria_list}
      />
    );
  }

  if (content.__component === "blocks.career-form-section") {
    const { form } = content as ICareerFormSection;

    if (!form) return null;

    return <CareerForm pageName={pageName} pathName={path} />;
  }

  if (content.__component === "blocks.schedule-meeting-section") {
    const { content: data, images, form } = content as IScheduleMeetSection;

    return (
      <ScheduleMeetingSection
        heading={data.heading}
        description={
          data.description ? (
            <BlocksRenderer content={data.description} />
          ) : undefined
        }
        images={images?.map((image) => ({
          src: getImageUrl(image),
          alt: image.name ?? "",
        }))}
        form={form}
      />
    );
  }

  if (content.__component === "blocks.team-spirit-section") {
    const { heading, description, image, cards } =
      content as ITeamSpiritSection;

    return (
      <TeamSpiritSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        teamSpirits={cards?.map(({ heading, description, position }) => ({
          heading,
          position,
          description,
        }))}
        image={
          image ? { src: getImageUrl(image), alt: image.name ?? "" } : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.text-with-card-grid-section") {
    const { heading, cards } = content as ITextWithCardGridSection;

    return (
      <TextWithCardGridSection
        heading={heading}
        list={cards?.map(({ description }) => description)}
      />
    );
  }

  if (content.__component === "blocks.team-section") {
    const { our_team } = content as ITeamSection;

    const { heading, description, team } = our_team?.data.attributes ?? {};

    return (
      <TeamSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        teamMembers={team?.team_members.data?.map(({ attributes }) => {
          const { image, name, designation, achievement, about, social_media } =
            attributes;
          return {
            name,
            image: { src: getImageUrl(image), alt: image.name ?? "" },
            designation,
            achievement,
            about,
            socialHandles: map(social_media, (val, key) => ({
              platform: socialMedia[key] as any, // type ignored, it didn't understand type autometically
              link: val,
            })),
          };
        })}
      />
    );
  }

  if (content.__component === "blocks.blogs") {
    const { blog_data, blog_section } = content as IBlogsSection;

    if (!blog_section) return;

    // Unified listing UI for both the new /blog/<category> URLs and the
    // legacy /leadership-insights + /technical-blogs paths (latter are
    // 301-redirected at the edge but still match here while the move
    // settles — keeping both in the path check means a redirect failure
    // or a manual visit doesn't fall through to FeaturedBlogsSection).
    if (path === "/blog/leadership" || path === "/leadership-insights") {
      return <BlogsWithFilterSection category="leadership" />;
    }
    if (path === "/blog/technical" || path === "/technical-blogs") {
      return <BlogsWithFilterSection category="technical" />;
    }

    return (
      <FeaturedBlogsSection
        showMoreBlogs={path.split("/")[1] === "leadership-insights"}
        blogs={blog_data?.map(({ attributes }) => ({
          publishDate: attributes.publish_date,
          readingTime: attributes.reading_time,
          heading: attributes.title,
          description: attributes.description ? (
            <BlocksRenderer content={attributes.description} />
          ) : undefined,
          image: {
            src: getImageUrl(attributes.image),
            alt: attributes.image.name ?? "",
          },
          author: {
            name: attributes.author?.data?.attributes.name,
            image: getImageUrl(attributes.author?.data?.attributes.image),
          },
          link: `${attributes.category === "technical" ? "/technical-blogs" : "/leadership-insights"}/${attributes.slug}`,
        }))}
      />
    );
  }

  if (content.__component === "blocks.latest-blog") {
    const { latest_blog_section } = content as ILatestBlogsSection;

    // Skip on listing pages (new /blog/<category> + legacy paths) —
    // those already render the full BlogsWithFilterSection grid, so a
    // "Latest Articles" block here is just a duplicate of the grid
    // immediately above it. Hides at code level so the Strapi page
    // entry can keep the block without anyone having to clean it up.
    const isListingPage =
      path === "/blog/leadership" ||
      path === "/blog/technical" ||
      path === "/leadership-insights" ||
      path === "/technical-blogs";

    if (latest_blog_section && !isListingPage) {
      return (
        <LatestBlogsSection heading="Latest Articles" initialBlogCount={9} />
      );
    }
  }

  if (content.__component === "blocks.course-location") {
    const { cards } = content as ICourseLocationSection;

    return (
      <CourseLocation
        locationData={cards?.map(({ time_line, heading, description }) => ({
          subHeading: time_line,
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.course-timing-section") {
    const { heading, description, cards } = content as ICourseTimingSection;

    return (
      <CourseTimingSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        scheduleCards={cards?.map(({ heading, description }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.offer-section") {
    const { heading, new_price, old_price } = content as IOfferSection;

    return (
      <OfferSection
        heading={heading}
        oldPrice={old_price}
        newPrice={new_price}
      />
    );
  }

  if (content.__component === "blocks.course-plan-section") {
    const { heading, weeks, button } = content as ICoursePlanSection;

    return (
      <CoursePlan
        heading={heading}
        weeks={weeks?.map(({ heading, description }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
        button={
          button
            ? {
                ...button,
                text: button.title,
                link: button.href,
                id: button.btn_id,
              }
            : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.upcoming-courses-section") {
    const { heading, courses } = content as IUpcomingCoursesSection;

    return (
      <UpcomingCoursesSection
        heading={heading}
        courses={courses?.data
          ?.map(({ attributes }) => ({
            heading: attributes.page_name,
            image: {
              src: getImageUrl(attributes.card_image),
              alt: attributes.card_image.name ?? "",
            },
            link: `/${path.split("/")[1]}/${attributes.slug}`,
          }))
          .slice(0, 3)}
      />
    );
  }

  if (content.__component === "blocks.service-list-section") {
    const {
      heading,
      description,
      card,
      content: data,
    } = content as IServiceListSection;

    return (
      <ServiceListSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        ctaSquareBox={{
          heading: card?.heading,
          description: card?.description ? (
            <BlocksRenderer content={card.description} />
          ) : undefined,
          button: {
            ...card?.button?.[0],
            text: card?.button?.[0].title,
            link: card?.button?.[0].href,
            id: card?.button?.[0].btn_id,
          },
        }}
        stages={data?.map(({ heading, description }) => ({
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
      />
    );
  }

  if (content.__component === "blocks.service-tab-list-section") {
    const { heading, card, service_tabs } = content as IServiceTabListSection;

    return (
      <ServiceTabListSection
        heading={heading}
        ctaSquareBox={{
          heading: card?.heading,
          description: card?.description ? (
            <BlocksRenderer content={card.description} />
          ) : undefined,
          button: {
            ...card?.button?.[0],
            text: card?.button?.[0].title,
            link: card?.button?.[0].href,
            id: card?.button?.[0].btn_id,
          },
        }}
        tabList={service_tabs?.map(({ heading }) => heading)}
        tabContent={service_tabs?.map(({ heading, content }) => ({
          heading,
          stages: content?.map(({ heading, description }) => ({
            heading,
            description: description ? (
              <BlocksRenderer content={description} />
            ) : undefined,
          })),
        }))}
      />
    );
  }

  if (content.__component === "blocks.take-security-assessment") {
    const { heading, description, cards, button } =
      content as ITakeSecurityAssessmentSection;

    return (
      <TakeSecurityAssessment
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        securityCards={cards.map(({ icon, description, heading }) => ({
          icon: icon.name,
          heading,
          description: description ? (
            <BlocksRenderer content={description} />
          ) : undefined,
        }))}
        button={
          button
            ? {
                ...button,
                text: button.title,
                link: button.href,
                id: button.btn_id,
              }
            : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.resource-download-form") {
    const { show_form, form } = content as IResourceDownloadFormSection;

    if (!show_form) return;
    if (!params) return;

    const category = params["slug"];
    const resourcePage = params["id"];

    return (
      <ResourceDownloadFormSection
        category={category}
        resourcePage={resourcePage}
        pageName={pageName}
      />
    );
  }

  if (content.__component === "blocks.call-cta-section") {
    const { heading, description, button } = content as ICallCtaSection;

    return (
      <CallCtaSection
        heading={heading}
        description={description}
        buttons={button?.map((button) => ({
          ...button,
          text: button.title,
          link: button.href,
          id: button.btn_id,
        }))}
      />
    );
  }

  if (content.__component === "blocks.happy-hours-section") {
    const { heading, description, images } = content as IHappyHoursSection;

    return (
      <GallerySection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        gallery={
          images
            ? images.map((imageGroup) => ({
                images: imageGroup.images?.map((image) => ({
                  src: getImageUrl(image),
                  alt: image.name,
                  height: image.image.data.attributes.height,
                  width: image.image.data.attributes.width,
                })),
              }))
            : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.course-overview-section") {
    const { heading, description, button, cards, video, video_link } =
      content as ICourseOverviewSection;

    return (
      <CourseOverviewSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        button={
          button
            ? {
                ...button,
                text: button.title,
                link: button.href,
                id: button.btn_id,
              }
            : undefined
        }
        cards={
          cards && cards.length > 0
            ? cards.map(({ sub_heading, heading, description }) => ({
                subHeading: sub_heading,
                heading,
                description: description ? (
                  <BlocksRenderer content={description} />
                ) : undefined,
              }))
            : undefined
        }
        video={getVideoUrl(video)}
        videoLink={video_link}
      />
    );
  }

  if (content.__component === "blocks.course-deliverable-section") {
    const { heading, cards } = content as ICourseDeliverables;

    return <CourseDeliverablesSection heading={heading} cards={cards} />;
  }

  if (content.__component === "blocks.cyber-security-section") {
    const { heading, phases, content: data } = content as ICyberSecuritySection;

    return (
      <CyberSecuritySection
        heading={heading}
        phases={phases}
        contentData={data}
      />
    );
  }

  if (content.__component === "blocks.security-iframe-section") {
    const { url } = content as ISecurityIframeSection;

    return <SecurityIframeSection url={url} />;
  }

  if (content.__component === "blocks.explore-category-section") {
    const { heading, by_function, by_process, by_role } =
      content as IExploreCategorySection;

    return (
      <ExploreCategoriesSection
        heading={heading}
        blogsByFunctions={{
          heading: by_function?.heading,
          items: by_function?.blogs_by_functions
            ? by_function.blogs_by_functions.data.map(
                (item) => item.attributes?.name,
              )
            : undefined,
        }}
        blogsByProcesses={{
          heading: by_process?.heading,
          items: by_process?.blogs_by_processes
            ? by_process.blogs_by_processes.data.map(
                (item) => item.attributes?.name,
              )
            : undefined,
        }}
        blogsByRoles={{
          heading: by_role?.heading,
          items: by_role?.blogs_by_roles
            ? by_role.blogs_by_roles.data.map((item) => item.attributes?.name)
            : undefined,
        }}
      />
    );
  }

  if (content.__component === "blocks.partners-form-section") {
    const { heading, description, show_form } = content as IPartnersFormSection;

    return (
      <PartnersFormSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        show_form={show_form}
      />
    );
  }

  if (content.__component === "blocks.custom-form") {
    const { custom_form } = content as ICustomFormSection;

    if (!custom_form.data) {
      return;
    }

    const { name, form_heading, fields, consents, form_id, description } =
      custom_form.data.attributes;

    return (
      <CustomFormSection
        name={name}
        fields={fields}
        consents={consents}
        formID={form_id}
        formHeading={form_heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
      />
    );
  }

  if (content.__component === "blocks.awards-section") {
    const { heading, description, awards } = content as IAwardsSection;

    return (
      <AwardsSection
        heading={heading}
        description={
          description ? <BlocksRenderer content={description} /> : undefined
        }
        awards={
          awards && awards.length > 0
            ? awards.map(({ heading, year, description, image }) => ({
                year,
                heading,
                description: description ? (
                  <BlocksRenderer content={description} />
                ) : undefined,
                image: image,
              }))
            : undefined
        }
      />
    );
  }

  // ---------------------------- reusable components ---------------------------

  if (content.__component === "blocks.reusable-section") {
    const { reusable_component } = content as IReusableSection;

    const { select_section_type, select_section } =
      reusable_component.data.attributes;

    if (select_section_type === "solution_section") {
      const { heading, description, SolutionCard } =
        select_section[0] as ISolutionSection;

      return (
        <SolutionSection
          heading={heading}
          description={
            description ? <BlocksRenderer content={description} /> : undefined
          }
          solutions={SolutionCard?.map(
            ({ icon, heading, description, links }) => ({
              icon: icon?.name,
              list: links,
              heading,
              description: description ? (
                <BlocksRenderer content={description} />
              ) : undefined,
            }),
          )}
        />
      );
    }

    if (select_section_type === "education_material_section") {
      const { heading, list } = select_section[0] as IEducationMaterialSection;

      // v2 redesign uses the raw rich-text list so it can extract embedded
      // links per item and render a 4-card resource directory. Old fields
      // (main_image, sub_image, button) are intentionally not passed —
      // they were the illustration + featured-CTA chrome from v1.
      return <EducationMaterialSection heading={heading} rawList={list} />;
    }

    if (select_section_type === "team_contribution_section") {
      const { heading, description, images } =
        select_section[0] as ITeamContributionSection;

      return (
        <TextWithImageGrid
          heading={heading}
          description={
            description ? <BlocksRenderer content={description} /> : undefined
          }
          images={images?.map((image) => ({
            src: getImageUrl(image),
            alt: image.name ?? "",
          }))}
          isTwoColumnSection={false}
        />
      );
    }

    if (select_section_type === "call_cta_section") {
      const { heading, description, button } =
        select_section[0] as ICallCtaSection;

      return (
        <CallCtaSection
          heading={heading}
          description={description}
          buttons={button?.map((button) => ({
            ...button,
            text: button.title,
            link: button.href,
            id: button.btn_id,
          }))}
        />
      );
    }

    if (select_section_type === "newsletter_section") {
      // Newsletter now lives sitewide inside the Footer (see Footer.tsx).
      // Returning null here makes the home page's Strapi `newsletter_section`
      // block a no-op so subscribers don't see the form twice (once mid-page,
      // once in the footer). Leave the block in Strapi if you want — this
      // just stops rendering it. To revert, restore the
      // <NewsLetterSubscribeSection .../> render below.
      return null;
    }
  }

  // ---------------------------- static components ---------------------------

  if (content.__component === "blocks.static-blocks-section") {
    switch ((content as IStaticBlocksSection).static_block_name) {
      case "maturity_model_section":
        return (
          <MaturityModelSection
            heading={maturityModelSectionData.heading}
            imageUrl={maturityModelSectionData.imageUrl}
          />
        );
      case "education_materials_section":
        // v2 redesign — static-content path doesn't currently feed the
        // resource directory. Returns null so the old prop shape stops
        // type-erroring. If you need this static rendering back, supply
        // a `rawList` matching the Strapi BlocksContent shape.
        return null;
      case "rating_section":
        return (
          <RatingSection
            heading={ratingSectionData.mainDescription.heading}
            description={ratingSectionData.mainDescription.description}
            buttons={ratingSectionData.mainDescription.buttons}
            certificateImages={
              ratingSectionData.certificatesAndRatings.certificates
            }
            ratingImage={ratingSectionData.certificatesAndRatings.rating}
          />
        );
      case "rating_section_light_blue":
        return (
          <RatingSection
            heading={ratingSectionData.mainDescription.heading}
            description={ratingSectionData.mainDescription.description}
            buttons={ratingSectionData.mainDescription.buttons}
            certificateImages={
              ratingSectionData.certificatesAndRatings.certificates
            }
            ratingImage={ratingSectionData.certificatesAndRatings.rating}
            variant="secondary"
          />
        );
      case "call_cta_section":
        return (
          <CallCtaSection
            heading={callCtaSectionData.heading}
            description={callCtaSectionData.description}
            buttons={callCtaSectionData.buttons}
          />
        );
      case "schedule_meet_section":
        // The pre-footer schedule-meet band is temporarily disabled —
        // industry / service pages already carry an explicit mid-page
        // call-cta-section, and the footer itself surfaces the
        // newsletter + contact info. Two CTAs stacked at the bottom of
        // the page were diluting conversion.
        //
        // Restore by reverting to:
        //   return (
        //     <ScheduleMeetSection
        //       heading={ScheduleMeetSectionData.heading}
        //       subHeading={ScheduleMeetSectionData.subHeading}
        //       button={ScheduleMeetSectionData.button}
        //     />
        //   );
        return null;
      case "teams_contribution":
        return (
          <TextWithImageGrid
            heading={textWithImageGridData.heading}
            description={textWithImageGridData.description}
            images={textWithImageGridData.images}
            isTwoColumnSection={false}
          />
        );
      case "vision_mission_section":
        return (
          <VisionMissionSection
            heading={missionVisionData?.heading}
            description={missionVisionData?.description}
            cards={missionVisionData?.cards}
          />
        );
      case "reporting_standard":
        return (
          <ReportingStandardSection
            heading={ReportingStandardSectionData.heading}
            description={ReportingStandardSectionData.description}
            logos={ReportingStandardSectionData.logos}
          />
        );
    }
  }
};

export default StandardComponent;
